/**
 * React hooks for hash management
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { hashManager } from './HashManager';
import type { HashChangeOptions, HashNavigationOptions } from './types';
import { log } from '@/utils/logger';

/**
 * Hook to get and manage current hash state
 */
export function useHash(): [string, (hash: string, options?: HashChangeOptions) => void] {
  const [hash, setHashState] = useState<string>('');

  useEffect(() => {
    // Initialize with current hash
    setHashState(hashManager.getCurrentHash());

    // Subscribe to hash changes
    const unsubscribe = hashManager.onHashChange(newHash => {
      setHashState(newHash);
    });

    return unsubscribe;
  }, []);

  const setHash = useCallback((newHash: string, options?: HashChangeOptions) => {
    hashManager.setHash(newHash, options);
  }, []);

  return [hash, setHash];
}

/**
 * Hook for hash-based navigation with Next.js router integration
 */
export function useHashNavigation() {
  const router = useRouter();
  const pathname = usePathname();

  // Setup router integration
  useEffect(() => {
    const handleNavigationEvent = (event: CustomEvent) => {
      const { path, replace, locale } = event.detail;

      log.debug({
        event: 'hash_navigation_router_integration',
        message: 'Processing navigation event',
        labels: { module: 'useHashNavigation' },
        stringify: { path, replace, locale },
      });

      if (replace) {
        router.replace(path);
      } else {
        router.push(path);
      }
    };

    window.addEventListener('hashManagerNavigate', handleNavigationEvent as EventListener);

    return () => {
      window.removeEventListener('hashManagerNavigate', handleNavigationEvent as EventListener);
    };
  }, [router]);

  const navigateWithHash = useCallback(
    (targetPathname: string, hash: string, options: HashNavigationOptions = {}) => {
      hashManager.navigateWithHash(targetPathname, hash, {
        ...options,
        pathname: pathname,
      });
    },
    [pathname]
  );

  const pushWithHash = useCallback(
    (hash: string, options?: HashNavigationOptions) => {
      const fullPath = hash ? `${pathname}#${hash.replace('#', '')}` : pathname;

      if (options?.replace) {
        router.replace(fullPath);
      } else {
        router.push(fullPath);
      }

      // Update hash manager state
      hashManager.setHash(hash, options);
    },
    [pathname, router]
  );

  const replaceWithHash = useCallback(
    (hash: string, options?: HashNavigationOptions) => {
      pushWithHash(hash, { ...options, replace: true });
    },
    [pushWithHash]
  );

  return {
    navigateWithHash,
    pushWithHash,
    replaceWithHash,
    currentPathname: pathname,
  };
}

/**
 * Hook for scroll-to-element functionality with hash
 */
export function useHashScroll(
  options: {
    offset?: number;
    smooth?: boolean;
  } = {}
) {
  const scrollToHash = useCallback(
    (hash: string) => {
      return hashManager.scrollToElement(hash, options);
    },
    [options]
  );

  const scrollToCurrent = useCallback(() => {
    const currentHash = hashManager.getCurrentHash();
    if (currentHash) {
      return scrollToHash(currentHash);
    }
    return false;
  }, [scrollToHash]);

  return {
    scrollToHash,
    scrollToCurrent,
    getCurrentElement: () => hashManager.getCurrentElement(),
  };
}

/**
 * Hook to listen for hash changes
 */
export function useHashListener(callback: (hash: string, element?: HTMLElement | null) => void, deps: unknown[] = []) {
  const callbackRef = useRef(callback);

  // Update callback ref when dependencies change
  useEffect(() => {
    callbackRef.current = callback;
  }, deps);

  useEffect(() => {
    const unsubscribe = hashManager.onHashChange((hash, element) => {
      callbackRef.current(hash, element);
    });

    return unsubscribe;
  }, []);
}

/**
 * Hook to check if a specific hash is currently active
 */
export function useIsHashActive(targetHash: string): boolean {
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    // Check initial state
    setIsActive(hashManager.isCurrentHash(targetHash));

    // Subscribe to changes
    const unsubscribe = hashManager.onHashChange(hash => {
      setIsActive(hash === targetHash.replace('#', ''));
    });

    return unsubscribe;
  }, [targetHash]);

  return isActive;
}
